import {
 Button
} from 'shared/components/ui/button';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    ArrowUp, ArrowDown, Plus, Trash2,
    Brain, Search, Globe, Database, GitFork, Workflow,
    Settings, FileOutput, Info, PhoneForwarded
} from 'lucide-react';
import {
 JsonOpcodeStep, FlowDefinition, TypedOutput, StepKind
} from 'builders/builderOpcode/jsonMode/types';
import {
 cn
} from 'shared/lib/utils';

export type SectionId = 'config' | 'metadata' | `step-${number}` | 'definitions' | 'flows' | 'output';

const STEP_ICONS: Record<StepKind, React.ElementType> = {
    llm: Brain,
    rag: Search,
    api: Globe,
    memory: Database,
    choose: GitFork,
    subflow: Workflow,
    parallel: GitFork,
    tool: Settings,
    call: PhoneForwarded,
};

const STEP_COLORS: Record<StepKind, string> = {
    llm: 'text-violet-500',
    rag: 'text-blue-500',
    api: 'text-green-500',
    memory: 'text-amber-500',
    choose: 'text-rose-500',
    subflow: 'text-cyan-500',
    parallel: 'text-fuchsia-500',
    tool: 'text-orange-500',
    call: 'text-indigo-500',
};

// Step-common controllers — chips shown on the step row so authors can see at
// a glance which steps loop, branch on a condition, or repeat. Matches engine
// vocabulary (`when`, `for`, `repeat_until`).
const hasController = (step: JsonOpcodeStep) => ({
    when: Boolean(step.when),
    forEach: Boolean(step.for),
    repeatUntil: Boolean(step.repeat_until),
});

const V2NavigationPanel = ({
    selectedSection,
    onSelectSection,
    opCodeName,
    inputs,
    steps,
    definitions,
    flows,
    outputs,
    onAddStep,
    onRemoveStep,
    onMoveStep,
}: {
    selectedSection: SectionId;
    onSelectSection: (section: SectionId) => void;
    opCodeName: string;
    inputs: { name: string }[];
    steps: JsonOpcodeStep[];
    definitions: JsonOpcodeStep[];
    flows: FlowDefinition[];
    outputs: TypedOutput[];
    onAddStep: () => void;
    onRemoveStep: (index: number) => void;
    onMoveStep: (index: number, direction: 'up' | 'down') => void;
}) => {
    return (
        <ScrollArea className="h-full">
            <div className="flex flex-col gap-1 p-3">
                {/* Config Section */}
                <button
                    onClick={() => onSelectSection('config')}
                    className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors w-full',
                        selectedSection === 'config'
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                    )}
                >
                    <Settings className="h-4 w-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                            {opCodeName || 'Configuration'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {inputs.length} input{inputs.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                </button>

                {/* Divider */}
                <div className="px-3 pt-3 pb-1">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Steps
                        </span>
                        <Badge variant="secondary" className="text-xs h-5">
                            {steps.length}
                        </Badge>
                    </div>
                </div>

                {/* Steps */}
                {steps.length === 0 && (
                    <div className="px-3 py-4 text-center">
                        <p className="text-xs text-muted-foreground mb-2">No steps yet</p>
                        <Button size="sm" variant="outline" onClick={onAddStep} className="w-full">
                            <Plus className="h-3 w-3 mr-1" />
                            Add First Step
                        </Button>
                    </div>
                )}

                {steps.map((step, index) => {
                    const Icon = STEP_ICONS[step.kind] || Settings;
                    const colorClass = STEP_COLORS[step.kind] || 'text-gray-500';
                    const sectionId: SectionId = `step-${index}`;
                    const isSelected = selectedSection === sectionId;
                    const ctrl = hasController(step);

                    return (
                        <div
                            key={step.id || index}
                            className={cn(
                                'group flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer',
                                isSelected
                                    ? 'bg-primary/10 text-primary border border-primary/20'
                                    : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                            )}
                            onClick={() => onSelectSection(sectionId)}
                        >
                            <Icon className={cn('h-4 w-4 shrink-0', colorClass)} />
                            <div className="flex-1 min-w-0">
                                <span className="font-medium">{index + 1}.</span>{' '}
                                <span className="capitalize">{step.kind}</span>
                            </div>
                            {ctrl.when && (
                                <Badge
                                    variant="outline"
                                    title="Skipped unless `when` evaluates truthy"
                                    className="text-[10px] h-5 shrink-0 px-1.5 font-normal text-rose-600 border-rose-200 bg-rose-50"
                                >
                                    if
                                </Badge>
                            )}
                            {ctrl.forEach && (
                                <Badge
                                    variant="outline"
                                    title="Runs once per item in the collection"
                                    className="text-[10px] h-5 shrink-0 px-1.5 font-normal text-blue-600 border-blue-200 bg-blue-50"
                                >
                                    for each
                                </Badge>
                            )}
                            {ctrl.repeatUntil && (
                                <Badge
                                    variant="outline"
                                    title="Repeats until the condition becomes truthy (capped by max_iters)"
                                    className="text-[10px] h-5 shrink-0 px-1.5 font-normal text-amber-700 border-amber-200 bg-amber-50"
                                >
                                    until
                                </Badge>
                            )}
                            <Badge variant="outline" className="font-mono text-[10px] h-5 shrink-0">
                                {step.id}
                            </Badge>

                            {/* Hover actions */}
                            <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5"
                                    disabled={index === 0}
                                    onClick={(e) => { e.stopPropagation(); onMoveStep(index, 'up'); }}
                                >
                                    <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5"
                                    disabled={index === steps.length - 1}
                                    onClick={(e) => { e.stopPropagation(); onMoveStep(index, 'down'); }}
                                >
                                    <ArrowDown className="h-3 w-3" />
                                </Button>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-destructive hover:text-destructive"
                                    onClick={(e) => { e.stopPropagation(); onRemoveStep(index); }}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    );
                })}

                {steps.length > 0 && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={onAddStep}
                        className="mx-3 mt-1 justify-start text-muted-foreground"
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Step
                    </Button>
                )}

                {/* Divider */}
                <div className="border-t my-2" />

                {/* Definitions */}
                <button
                    onClick={() => onSelectSection('definitions')}
                    className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors w-full',
                        selectedSection === 'definitions'
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                    )}
                >
                    <GitFork className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Definitions</span>
                    {definitions.length > 0 && (
                        <Badge variant="secondary" className="text-xs h-5">
                            {definitions.length}
                        </Badge>
                    )}
                </button>

                {/* Flows */}
                <button
                    onClick={() => onSelectSection('flows')}
                    className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors w-full',
                        selectedSection === 'flows'
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                    )}
                >
                    <Workflow className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Flows</span>
                    {flows.length > 0 && (
                        <Badge variant="secondary" className="text-xs h-5">
                            {flows.length}
                        </Badge>
                    )}
                </button>

                {/* Divider */}
                <div className="border-t my-2" />

                {/* Output */}
                <button
                    onClick={() => onSelectSection('output')}
                    className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors w-full',
                        selectedSection === 'output'
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                    )}
                >
                    <FileOutput className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Outputs</span>
                    {outputs.length > 0 && (
                        <Badge variant="secondary" className="text-xs h-5">
                            {outputs.length}
                        </Badge>
                    )}
                </button>

                {/* Divider */}
                <div className="border-t my-2" />

                {/* Metadata (informational) */}
                <button
                    onClick={() => onSelectSection('metadata')}
                    className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors w-full',
                        selectedSection === 'metadata'
                            ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                            : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                    )}
                >
                    <Info className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Metadata</span>
                    <Badge variant="outline" className="text-[10px] h-5 uppercase">
                        Info
                    </Badge>
                </button>
            </div>
        </ScrollArea>
    );
};

export default V2NavigationPanel;
